import type { CatalogProperty } from 'src/api/generated';
import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { Text } from 'src/modules/shared/components/atoms/text';
import React from 'react';
import { PropertyTypeSelect } from './property-type-select';
import {
  Controller,
  type Control,
  type UseFormRegister,
} from 'react-hook-form';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import { PropertyEditorSectionCard } from './property-editor-section-card';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import type { PropertyFormInputValues } from '../../validations/property-validations';

/**
 * Props for the property overview editor section.
 */
interface PropertyEditorOverviewSectionProps {
  /** Existing property reference displayed in the summary. */
  property?: CatalogProperty | null;
  /** Assignment control or readonly value composed by the editor form. */
  assigneeField?: React.ReactNode;
  /** Form control instance for controlled inputs. */
  control: Control<PropertyFormInputValues>;
  /** Form register function for native inputs. */
  register: UseFormRegister<PropertyFormInputValues>;
  /** Validation message displayed below the property title. */
  nameError?: string;
  /** Revalidates only the title as the user edits it. */
  onNameChange: () => void;
  /** Available operation options for the property select. */
  operationOptions: { content: string; value: string }[];
  /** Available property type options for the property select. */
  propertyTypeOptions: { content: string; value: string }[];
  /** Available condition options for the property select. */
  propertyConditionOptions: { content: string; value: string }[];
  /** Translation function scoped to the current locale. */
  t: I18nContextValue['t'];
}

/**
 * Overview section for the property editor.
 */
export const PropertyEditorOverviewSection: React.FC<
  PropertyEditorOverviewSectionProps
> = ({
  property,
  assigneeField,
  control,
  register,
  nameError,
  onNameChange,
  operationOptions,
  propertyTypeOptions,
  propertyConditionOptions,
  t,
}) => {
  return (
    <PropertyEditorSectionCard
      title={t('catalog.properties.editor.sections.overview')}
      description={t('catalog.properties.editor.sections.overviewDescription')}
      actions={
        property ? (
          <Text size="small">
            <PropertyCode
              displayCode={property.displayCode}
              fallback={`#${property.id}`}
            />
          </Text>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1 flex flex-col gap-1">
          <Label size="md" required>
            {t('catalog.properties.form.name')}
          </Label>
          <TextInput
            {...register('name', { onChange: onNameChange })}
            placeholder={t('catalog.properties.editor.placeholders.name')}
            aria-label={t('catalog.properties.form.name')}
            aria-invalid={!!nameError}
            error={nameError}
            required
          />
        </div>
        {assigneeField ? (
          <div className="w-full lg:w-64 lg:shrink-0">{assigneeField}</div>
        ) : null}
        <Controller
          control={control}
          name="isActive"
          render={({ field: { value, onChange } }) => (
            <LabelSwitch
              label={t('catalog.properties.form.isActive')}
              checked={value ?? false}
              onChange={onChange}
              labelPosition="top"
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label size="md">{t('catalog.properties.form.operation')}</Label>
          <Controller
            control={control}
            name="operation"
            render={({ field: { onChange, value } }) => (
              <Select
                options={operationOptions}
                value={value}
                onChange={(event) => onChange(event.target.value)}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label size="md">{t('catalog.properties.form.propertyType')}</Label>
          <PropertyTypeSelect control={control} options={propertyTypeOptions} />
        </div>

        <div className="flex flex-col gap-1">
          <Label size="md">{t('catalog.properties.form.condition')}</Label>
          <Controller
            control={control}
            name="condition"
            render={({ field: { onChange, value } }) => (
              <Select
                options={propertyConditionOptions}
                value={value}
                onChange={(event) => onChange(event.target.value)}
              />
            )}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Label size="md">{t('catalog.properties.form.description')}</Label>
        <TextArea
          {...register('description')}
          placeholder={t('catalog.properties.editor.placeholders.description')}
          rows={5}
        />
      </div>
    </PropertyEditorSectionCard>
  );
};
